// ============================================================================
// TENANT APPLICATION ASSIGNMENT ENDPOINTS
// ============================================================================
// Endpoint URL builders for the Tenant Application Assignments module.
//
// Two backends:
//   - Cross-tenant queries: /tenant-app-assignments (TenantAppAssignmentsController)
//   - Per-record CRUD:      /tenants/{tenantId}/applications (TenantApplicationsController)

import { buildEndpoint, buildEndpointWithParams } from './base.endpoints';
import type { ID } from '../types';

const BASE_PATH = '/tenant-app-assignments';
const PER_TENANT_BASE = '/tenants';

export const tenantAppAssignmentEndpoints = {
  /**
   * Cross-tenant paginated/filterable list.
   * GET /tenant-app-assignments
   */
  list: () => buildEndpoint(BASE_PATH),

  /**
   * Cross-tenant aggregate counts.
   * GET /tenant-app-assignments/statistics
   */
  statistics: () => buildEndpoint(`${BASE_PATH}/statistics`),

  /**
   * Get a single assignment for a tenant.
   * GET /tenants/:tenantId/applications/:appId
   */
  getById: (tenantId: ID, appId: ID) =>
    buildEndpointWithParams(`${PER_TENANT_BASE}/:tenantId/applications/:appId`, {
      tenantId,
      appId,
    }),

  /**
   * Create a new assignment under a tenant.
   * POST /tenants/:tenantId/applications
   */
  create: (tenantId: ID) =>
    buildEndpointWithParams(`${PER_TENANT_BASE}/:tenantId/applications`, { tenantId }),

  /**
   * Update an assignment.
   * PUT /tenants/:tenantId/applications/:appId
   */
  update: (tenantId: ID, appId: ID) =>
    buildEndpointWithParams(`${PER_TENANT_BASE}/:tenantId/applications/:appId`, {
      tenantId,
      appId,
    }),

  /**
   * Soft-remove an assignment.
   * DELETE /tenants/:tenantId/applications/:appId
   */
  remove: (tenantId: ID, appId: ID) =>
    buildEndpointWithParams(`${PER_TENANT_BASE}/:tenantId/applications/:appId`, {
      tenantId,
      appId,
    }),

  /**
   * List assignments for a single tenant (used by getAvailableApps to compute
   * the "not yet assigned" application set).
   * GET /tenants/:tenantId/applications
   */
  listByTenant: (tenantId: ID) =>
    buildEndpointWithParams(`${PER_TENANT_BASE}/:tenantId/applications`, { tenantId }),
};
